import { isEmpty, omit, isNull } from 'lodash';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import { ISidebarFilterOptionsContext } from '../../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { ISavedFilter } from '../../../../components/sidebar-filters/saved-filter/interface';
import { TAgreementStatusesFilterKey, AgreementStatusesFilterKeys } from './AgreementStatusesSidebarFilters';

export const transformAgreementStatusesSavedFilterToUser = (
    filter: ISavedFilter['filter_values'],
    context: ISidebarFilterOptionsContext
): Record<TAgreementStatusesFilterKey, string> => {
    let result: Record<TAgreementStatusesFilterKey, string> = {} as Record<TAgreementStatusesFilterKey, string>;

    for (const [key, value] of Object.entries(filter)) {
        // Filter out deselected values to avoid displaying them as "unknown"
        if (isUndefined(value) || isNull(value)) continue;

        switch (key) {
            case AgreementStatusesFilterKeys.company_id:
                result[key] = value
                    .map((val: string) => {
                        const option = context.allCompaniesOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case AgreementStatusesFilterKeys.agreement_status:
                result[key] = context.agreementStatusOptions.find(option => option.value === value)?.label || 'unknown';

                break;
            case AgreementStatusesFilterKeys.source:
                result[key] = value
                    .map((val: string) => {
                        const option = context.intervalDataSourceOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;

            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    // Remove empty properties from the result object
    const emptyProperties = getEmptyFilterProperties(result);
    if (emptyProperties.length > 0) {
        result = omit(result, emptyProperties) as Record<TAgreementStatusesFilterKey, string>;
    }

    return result;
};

export const transformAgreementStatusesSavedFilterValuesFromDto = (
    filter: ISavedFilter['filter_values']
): Record<TAgreementStatusesFilterKey, unknown> => {
    const result: Record<TAgreementStatusesFilterKey, unknown> = {} as Record<TAgreementStatusesFilterKey, unknown>;

    // Convert values from DTO format to interface format
    for (const [key, value] of Object.entries(filter)) {
        if (isUndefined(value)) continue;

        switch (key) {
            case AgreementStatusesFilterKeys.company_id:
            case AgreementStatusesFilterKeys.source:
            case AgreementStatusesFilterKeys.agreement_status:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = value; // Keep other values as is
        }
    }

    return result;
};

export const transformAgreementStatusesSavedFilterValuesToDto = (
    filter: ISavedFilter['filter_values']
): Record<TAgreementStatusesFilterKey, unknown> => {
    const result: Record<TAgreementStatusesFilterKey, unknown> = {} as Record<TAgreementStatusesFilterKey, unknown>;

    for (const [key, value] of Object.entries(filter)) {
        switch (key) {
            case AgreementStatusesFilterKeys.company_id:
            case AgreementStatusesFilterKeys.source:
            case AgreementStatusesFilterKeys.agreement_status:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    return result;
};

export const checkFilterInvalidValues = (filter: ISavedFilter['filter_values'], context: ISidebarFilterOptionsContext) => {
    const invalidFields: string[] = [];

    for (const [key, value] of Object.entries(filter)) {
        if (isNil(value) || isEmpty(value)) continue;

        switch (key) {
            case AgreementStatusesFilterKeys.company_id:
                if (Array.isArray(value)) {
                    const validOptionValues = context.allCompaniesOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case AgreementStatusesFilterKeys.agreement_status:
                if (typeof value === 'string' && !context.agreementStatusOptions.some(opt => opt.value === value)) {
                    invalidFields.push(key);
                }
                break;
            case AgreementStatusesFilterKeys.source:
                if (Array.isArray(value)) {
                    const validOptionValues = context.intervalDataSourceOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
        }
    }

    return invalidFields;
};

export function countNotEmptyFilters(filters: Record<string, any>): number {
    if (!filters) return 0;

    return Object.values(filters).filter(value => {
        if (Array.isArray(value)) return value.length > 0;

        if (typeof value === 'number') return !isNaN(value);

        if (typeof value === 'string') return value.trim() !== '';

        if (typeof value === 'boolean') return true;

        return false;
    }).length;
}

function getEmptyFilterProperties(obj: Record<TAgreementStatusesFilterKey, string>): TAgreementStatusesFilterKey[] {
    return Object.entries(obj)
        .filter(([_, value]) => isEmpty(value))
        .map(([key, _]) => key) as TAgreementStatusesFilterKey[];
}
